import { doc, onSnapshot } from "firebase/firestore";
import { authedFetch } from "./onlineClient";
import { authReady, db } from "./firebase";
import { normalizeVoiceRoom, type VoiceRoster } from "../voice/rules";
import type { VoiceAgeBand } from "../voice/consent";

/*
 * The voice routes, one typed call each. The server's half of the contract is
 * docs/VOICE_CHAT.md's "Routes" cut down for v1.
 *
 * `roomId` is ALWAYS the lobby game id - the party parent in a party - and the
 * server answers an instance id with 400 "use the lobby id". The caller's
 * identity is the verified token alone, so none of these send X-Player-Id.
 *
 * Errors arrive the way every route's do: authedFetch throws an Error whose
 * message is the server's `error` string, and for these routes that string is a
 * code (`voice-off`, `policy`, ...) rather than a sentence; see voiceErrorCode.
 */

export interface SessionDescription {
  type: RTCSdpType;
  sdp: string;
}

export interface VoiceJoinResponse {
  roomId: string;
  iceServers: RTCIceServer[];
}

export interface PulledTrack {
  playerId: string;
  trackGen: number;
  mid?: string;
  error?: string;
}

export interface VoicePullResponse {
  tracks: PulledTrack[];
  sessionDescription?: SessionDescription;
  requiresImmediateRenegotiation: boolean;
}

export interface VoiceCloseResponse {
  sessionDescription?: SessionDescription;
  requiresImmediateRenegotiation: boolean;
}

/** The refusals the client acts on rather than just reporting. */
export type VoiceErrorCode =
  | "voice-off"
  | "not-in-game"
  | "policy"
  | "age"
  | "not-joined"
  | "bad-sdp"
  | "slow-down"
  | "table-off"
  | "host-muted"
  | "not-host"
  /** Cloudflare has ended this client's session; nothing on it can succeed again. */
  | "session-gone";

const CODES: readonly string[] = [
  "voice-off",
  "not-in-game",
  "policy",
  "age",
  "not-joined",
  "table-off",
  "host-muted",
  "not-host",
  "session-gone",
];

export function voiceErrorCode(err: unknown): VoiceErrorCode | null {
  const message = err instanceof Error ? err.message : String(err);
  if (CODES.includes(message)) return message as VoiceErrorCode;
  if (message.startsWith("bad-sdp")) return "bad-sdp";
  if (message === "slow down") return "slow-down";
  return null;
}

const post = (path: string, body: unknown, init?: RequestInit) =>
  authedFetch(path, { method: "POST", body: JSON.stringify(body), ...init });

const voicePath = (roomId: string, op: string) => `/games/${encodeURIComponent(roomId)}/voice/${op}`;

/** Records that this account has read the policy, and the age band if the voice sheet asked it. */
export async function acknowledgePrivacy(materialVersion: number, ageBand?: VoiceAgeBand): Promise<void> {
  await post("/privacy/acknowledge", ageBand ? { materialVersion, ageBand } : { materialVersion });
}

export async function voiceJoin(roomId: string): Promise<VoiceJoinResponse> {
  const body = (await post(voicePath(roomId, "join"), {})) as Partial<VoiceJoinResponse>;
  return {
    roomId: typeof body.roomId === "string" ? body.roomId : roomId,
    iceServers: Array.isArray(body.iceServers) ? body.iceServers : [],
  };
}

/** The connection's first exchange: OUR offer of one trackless sendonly anchor section; the answer comes back. */
export async function voiceConnect(roomId: string, sdp: string): Promise<SessionDescription | null> {
  const body = (await post(voicePath(roomId, "connect"), { sdp })) as { sessionDescription?: SessionDescription };
  return body.sessionDescription?.sdp ? body.sessionDescription : null;
}

/** Publishes the mic. `sdp` is OUR offer; the answer comes back. */
export async function voicePublish(roomId: string, sdp: string, mid: string): Promise<SessionDescription | null> {
  const body = (await post(voicePath(roomId, "publish"), { sdp, mid })) as { sessionDescription?: SessionDescription };
  return body.sessionDescription?.sdp ? body.sessionDescription : null;
}

export async function voicePull(roomId: string, playerIds: string[]): Promise<VoicePullResponse> {
  const body = (await post(voicePath(roomId, "pull"), { playerIds })) as Partial<VoicePullResponse>;
  return {
    tracks: Array.isArray(body.tracks) ? body.tracks : [],
    sessionDescription: body.sessionDescription?.sdp ? body.sessionDescription : undefined,
    requiresImmediateRenegotiation: !!body.requiresImmediateRenegotiation,
  };
}

/** Sends OUR answer to an offer Cloudflare made. */
export async function voiceRenegotiate(roomId: string, sdp: string): Promise<void> {
  await post(voicePath(roomId, "renegotiate"), { sdp });
}

/** Closes pulled tracks. `sdp` is OUR offer, made after stopping those transceivers. */
export async function voiceClose(roomId: string, mids: string[], sdp: string): Promise<VoiceCloseResponse> {
  const body = (await post(voicePath(roomId, "close"), { mids, sdp })) as Partial<VoiceCloseResponse>;
  return {
    sessionDescription: body.sessionDescription?.sdp ? body.sessionDescription : undefined,
    requiresImmediateRenegotiation: !!body.requiresImmediateRenegotiation,
  };
}

export async function voiceState(roomId: string, selfMuted: boolean): Promise<void> {
  await post(voicePath(roomId, "state"), { selfMuted });
}

/**
 * The host mutes one player for everyone (`playerId`), or everyone at once
 * (`all`). Host only - the server checks the token against the lobby's host
 * seat - and it works whether or not the player is in voice: the lists persist,
 * so a later join arrives muted.
 */
export async function voiceHostMute(roomId: string, target: { playerId: string; muted: boolean } | { all: boolean }): Promise<void> {
  await post(voicePath(roomId, "host-mute"), target);
}

/** The host turns voice off (or back on) for the whole table. Off hangs everyone up. */
export async function voiceTable(roomId: string, off: boolean): Promise<void> {
  await post(voicePath(roomId, "table"), { off });
}

/**
 * `keepalive` for the pagehide path: a plain fetch started while the page is
 * being torn down is cancelled with it, and the roster would then show a player
 * who has gone until the next prune.
 */
export async function voiceLeave(roomId: string, opts: { keepalive?: boolean } = {}): Promise<void> {
  await post(voicePath(roomId, "leave"), {}, opts.keepalive ? { keepalive: true } : undefined);
}

/**
 * Watches `voice/{roomId}`, the roster every client reconciles its pulls
 * against.
 *
 * The same two rules as subscribeToGame, for the same reasons: attach only once
 * auth is up (the rules require a signed-in reader), and treat an errored
 * listener as dead and re-attach with backoff, because Firestore never
 * re-attaches one itself. A missing document is an empty room - nobody has
 * joined yet - not an error.
 */
export function subscribeToVoiceRoom(roomId: string, onRoom: (room: VoiceRoster) => void): () => void {
  let cancelled = false;
  let unsubscribe: (() => void) | null = null;
  let retryTimer: number | undefined;
  let attempt = 0;

  const scheduleRetry = () => {
    if (cancelled) return;
    const delay = Math.min(15000, 500 * 2 ** attempt++);
    retryTimer = window.setTimeout(attach, delay);
  };

  function attach() {
    if (cancelled) return;
    authReady().then(() => {
      if (cancelled) return;
      unsubscribe = onSnapshot(
        doc(db, "voice", roomId),
        (snap) => {
          attempt = 0;
          // Only an empty room from the server is believed; offline, the cache
          // answers "missing" for a document it has never seen.
          if (!snap.exists() && snap.metadata.fromCache) return;
          onRoom(normalizeVoiceRoom(snap.exists() ? snap.data() : null));
        },
        () => {
          unsubscribe = null;
          scheduleRetry();
        },
      );
    }, scheduleRetry);
  }

  attach();

  return () => {
    cancelled = true;
    if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    unsubscribe?.();
  };
}
